import React from "react";
import { Link } from "react-router-dom";
import { useTenant } from "../context/TenantContext";

interface TenantBrandingProps {
  className?: string;
  showText?: boolean;
  size?: "sm" | "md" | "lg";
}

const getSizeClasses = (size: TenantBrandingProps["size"]) => {
  switch (size) {
    case "sm":
      return "h-6 w-6";
    case "lg":
      return "h-12 w-12";
    case "md":
    default:
      return "h-8 w-8";
  }
};

export const TenantLogo: React.FC<TenantBrandingProps> = ({
  className = "",
  showText = true,
  size = "md"
}) => {
  const { branding } = useTenant();
  const sizeClasses = getSizeClasses(size);

  if (branding.logoUrl) {
    return (
      <img
        src={branding.logoUrl}
        alt={`${branding.displayName} logo`}
        className={`${sizeClasses} object-contain ${className}`}
      />
    );
  }

  if (!showText) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-xl font-semibold ${className}`}
      style={{
        backgroundColor: branding.primarySoft,
        color: branding.primaryColor,
        minWidth: size === "lg" ? "3rem" : size === "md" ? "2.5rem" : "2rem",
        minHeight: size === "lg" ? "3rem" : size === "md" ? "2.5rem" : "2rem",
        padding: "0 0.75rem"
      }}
    >
      {branding.displayName}
    </span>
  );
};

export const TenantHeader: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { branding } = useTenant();

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <TenantLogo size="md" />
      <div className="flex flex-col">
        <span className="font-semibold text-lg text-gray-900">{branding.portalTitle}</span>
        {branding.portalHostLabel && (
          <span className="text-sm text-gray-500">{branding.portalHostLabel}</span>
        )}
      </div>
    </div>
  );
};

export const TenantWelcome: React.FC<{ userName?: string; className?: string }> = ({
  userName,
  className = ""
}) => {
  const { tenant, branding } = useTenant();

  return (
    <div className={`text-center ${className}`}>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to {branding.displayName}</h1>
      {userName && <p className="text-gray-600">Hello, {userName}! Welcome back to your dashboard.</p>}
      {tenant?.status === "trial" && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-800 text-sm">
            You are currently in trial mode. Contact support to upgrade your subscription.
          </p>
        </div>
      )}
    </div>
  );
};

export const TenantSubscriptionAlert: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { tenant } = useTenant();
  const role = (localStorage.getItem("role") || "").toLowerCase();

  if (!tenant || tenant.is_subscription_exempt) {
    return null;
  }

  const getAlertContent = (): { type: "warning" | "error" | "info"; message: string } | null => {
    switch (tenant.status) {
      case "trial": {
        const trialEnd = tenant.trial_ends_at ? new Date(tenant.trial_ends_at) : null;
        const daysLeft = trialEnd ? Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
        if (daysLeft !== null && daysLeft <= 7) {
          return {
            type: "warning",
            message: `Your trial ends in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}. Please upgrade to continue using the platform.`
          };
        }
        return null;
      }
      case "expired":
        return {
          type: "error",
          message: "Your subscription has expired. Please renew to continue accessing the platform."
        };
      case "suspended":
        return {
          type: "error",
          message: "Your account has been suspended. Please contact support for assistance."
        };
      default:
        return null;
    }
  };

  const alert = getAlertContent();
  if (!alert) {
    return null;
  }

  const alertClasses = {
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    error: "bg-red-50 border-red-200 text-red-800",
    info: "bg-blue-50 border-blue-200 text-blue-800"
  };

  return (
    <div className={`p-4 border rounded-md ${alertClasses[alert.type]} ${className}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="ml-3">
          <p className="text-sm">{alert.message}</p>
        </div>
        {role === "admin" && (
          <Link
            to="/settings/subscription"
            className="inline-flex items-center rounded-xl border border-current/20 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em]"
          >
            Review Subscription
          </Link>
        )}
      </div>
    </div>
  );
};

export const TenantFooter: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { branding } = useTenant();

  return (
    <footer className={`text-center text-gray-500 text-sm ${className}`}>
      <p>(c) 2026 {branding.displayName}. Powered by the school portal platform.</p>
      {branding.portalHostLabel && <p className="mt-1">Portal: {branding.portalHostLabel}</p>}
    </footer>
  );
};

const TenantBranding = {
  TenantLogo,
  TenantHeader,
  TenantWelcome,
  TenantSubscriptionAlert,
  TenantFooter
};

export default TenantBranding;
